import { useRouter } from 'next/router';
import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useProductUsageQuery } from '../../components/ProductUsage/operation.generated';
import EventsTrackingService, {
  EventName as EventNameEnum,
} from '../../services/EventTrackingService';
import { useUserContext } from '../user/UserContext';
import { useAppSelector } from '../../store/hooks';
import { getDriveTypeFromName } from '../../components/Drives/utils';

/**
 * EventsContext provides access to our EventsTrackingService and manages some of the initialization
 * of said service.
 *
 * NOTE: this context depends on the `UserContext`
 */

type ContextState = typeof EventsTrackingService;

type ContextProps = [ContextState];

const getInitialState = (): ContextState => EventsTrackingService;

export const EventName = EventNameEnum;

export const EventsContext = createContext<ContextProps>([getInitialState()]);

EventsContext.displayName = 'EventsContext';

export function useEventsContext(): ContextProps {
  return useContext(EventsContext);
}

export const EventsProvider = ({ children }: { children: React.ReactNode }) => {
  const [events] = useState<ContextState>(getInitialState());
  const eventsContextValue: ContextProps = useMemo(() => [events], [events]);
  const [initialized, setInitialized] = useState<boolean>(false);
  const { data: userData } = useUserContext();
  const { currentDrive } = useAppSelector(state => state.drives);

  const { data: usageData } = useProductUsageQuery();
  const router = useRouter();

  /**
   * Initialization (on mount)
   */
  useEffect(() => {
    events.init().then(() => {
      setInitialized(true);
    });
    return () => {
      events.destroy();
    };
  }, [events]);

  /**
   * On user context change, update events service
   */
  useEffect(() => {
    if (initialized && userData && userData.user.id !== events.identity) {
      events.setUserIdentity(userData.user.id);
      const name = userData.user.profile.name || '';
      const nameSplit = name.split(' ');
      events.trackUserProfile({
        email: userData.user.profile.email,
        signUpDate: userData.user.profile.createdAt || undefined,
        name: name || undefined,
        firstName: nameSplit[0] || undefined,
        lastName: nameSplit[nameSplit.length - 1] || undefined,
        avatar: userData.user.profile.picture || undefined,
        clipsGenerated: userData.user.clipsGenerated,
        teamId: userData.user.team?.id,
        teamRole: userData.user.team?.viewersRoleOnTeam ?? undefined,
      });
    }
  }, [initialized, userData, events]);

  /**
   * Track whenever the user subscription plan changes
   */
  useEffect(() => {
    if (initialized && userData) {
      events.trackSubscriptionTier({
        tier: userData.user.subscription?.plan.displayName || '',
      });
    }
  }, [events, initialized, userData]);

  /**
   * Track whenever the user clipsGenerated count changes
   */
  useEffect(() => {
    if (initialized && usageData) {
      events.trackClipsGenerated({
        clipsCount: usageData.user.clipsGenerated,
      });
    }
  }, [events, initialized, usageData]);

  const pathname = router?.pathname;
  const driveType = useMemo(
    () => (currentDrive ? getDriveTypeFromName(currentDrive) : ''),
    [currentDrive]
  );
  const page =
    pathname?.replace(
      /\/\[workspaceId\](?=\/|$)/,
      `/${driveType?.toLowerCase()}`
    ) ?? '';

  /**
   * Track page-views
   */
  useEffect(() => {
    // `Router.on` events just pass the `url` to the handler. We don't want to track
    // `/project/abcdefg012`, instead we're tracking `/project/[projectId]` for better
    // grouping in mixpanel. For this reason, we're using `pathname` instead of `on` events.

    events.track({
      type: EventName.PageView,
      data: {
        page,
      },
    });
  }, [events, page]);

  return (
    <EventsContext.Provider value={eventsContextValue}>
      {children}
    </EventsContext.Provider>
  );
};
